import {
  detectComposeMode,
  insertSignatureIntoComposeBody,
  InsertSignatureResult
} from "../services/outlookComposeService";
import { signatureService } from "../services/signatureService";
import { clearProfileCache } from "../services/graphService";

declare const Office: any;

const statusElement = document.getElementById("status") as HTMLParagraphElement;
const composeModeElement = document.getElementById("composeMode") as HTMLParagraphElement;
const messageElement = document.getElementById("message") as HTMLParagraphElement;
const insertButton = document.getElementById("insertSignatureButton") as HTMLButtonElement;

/**
 * Detect if running on mobile web browser
 */
function isMobileDevice(): boolean {
  const userAgent = navigator.userAgent.toLowerCase();
  return /android|webos|iphone|ipad|ipot|blackberry|iemobile|opera mini/i.test(userAgent);
}

function setMessage(text: string, kind: "success" | "error" | "") {
  messageElement.textContent = text;
  messageElement.classList.remove("success", "error");
  if (kind) {
    messageElement.classList.add(kind);
  }
}

function setStatus(text: string): void {
  statusElement.textContent = `Status: ${text}`;
}

function setComposeMode(text: string): void {
  composeModeElement.textContent = `Compose mode: ${text}`;
}

async function initialize(): Promise<void> {
  try {
    const isMobile = isMobileDevice();
    const composeMode = await detectComposeMode();
    setStatus("Add-in loaded successfully");
    setComposeMode(composeMode);

    // Show mobile message if applicable
    if (isMobile) {
      setMessage(
        "⚠️ Mobile browser detected: Automatic signature insertion is not supported. Use the button below to insert manually.",
        ""
      );
    }

    // Load and display preview of user's signature
    await loadPreviewSignature();

    insertButton.disabled = false;
  } catch (error) {
    console.error("Initialization failed.", error);
    setStatus("Unable to initialize compose context.");
    setComposeMode("unknown");
    setMessage("Unable to initialize Outlook compose context.", "error");
    insertButton.disabled = true;
  }
}

/**
 * Loads and displays a preview of the user's signature
 * Fetches from Graph via signatureService
 */
async function loadPreviewSignature(): Promise<void> {
  try {
    const payload = await signatureService.getSignatureForCurrentUser();

    // Update preview elements with actual user data
    const nameEl = document.getElementById("previewName");
    const titleEl = document.getElementById("previewTitle");
    const phoneEl = document.getElementById("previewPhone");

    if (nameEl) nameEl.textContent = payload.name;
    if (titleEl) titleEl.textContent = payload.jobTitle || "Employee";
    if (phoneEl) phoneEl.textContent = `Phone: ${payload.phone || "N/A"}`;

    console.log("[TaskPane] Signature preview loaded:", {
      name: payload.name,
      jobTitle: payload.jobTitle,
      phone: payload.phone
    });
  } catch (error) {
    console.warn("[TaskPane] Failed to load preview signature:", error);
    // Keep default "Loading..." text if fetch fails
    const nameEl = document.getElementById("previewName");
    if (nameEl) nameEl.textContent = "Your Name (Click Insert to load)";
  }
}

async function onInsertSignatureClick(): Promise<void> {
  setMessage("", "");
  insertButton.disabled = true;
  setStatus("Fetching your signature from Microsoft Graph...");

  try {
    const result: InsertSignatureResult = await insertSignatureIntoComposeBody();

    if (result === "inserted") {
      setStatus("Add-in loaded successfully");
      setMessage("Signature inserted successfully.", "success");
    } else if (result === "already-present") {
      setStatus("Add-in loaded successfully");
      setMessage("Signature is already in this email.", "");
    } else {
      setStatus("Add-in loaded successfully");
      setMessage("Unable to insert the signature. Please try again.", "error");
    }
  } catch (error) {
    console.error("Signature insertion failed.", error);
    setStatus("Add-in loaded successfully");
    setMessage(
      `Error: ${error instanceof Error ? error.message : "Failed to fetch signature from Microsoft Graph. Make sure Microsoft 365 tenant has Graph API permissions configured."}`,
      "error"
    );
  } finally {
    insertButton.disabled = false;
  }
}

Office.onReady((info: { host?: string; platform?: string } | any) => {
  if (info.host === Office.HostType.Outlook) {
    insertButton.addEventListener("click", () => {
      void onInsertSignatureClick();
    });
    void initialize();
    return;
  }

  setStatus("This add-in is only supported in Outlook.");
  setComposeMode("not-applicable");
  insertButton.disabled = true;
});
